<template>
  <DefaultPageContainer
    :cnc-ads-page="cncAdsPage"
    :breadcrumb-text="author?.fullname as string"
  >
    <section class="section">
      <DefaultTwoColumnsContent
        right-container-class="section__right-col--mobile-hide"
      >
        <template #left>
          <!-- Author info -->
          <div v-if="author" class="author-id">
            <div class="flex justify-between">
              <div class="badge badge--author-id">
                <div class="badge__aside">
                  <img
                    loading="lazy"
                    v-if="getAvatar() && !imageError"
                    class="object-cover h-full w-full"
                    :src="getAvatar()"
                    :alt="author?.fullname || ''"
                    width="60"
                    height="60"
                    @error="replaceByDefault"
                  >
                  <!--no profile-pic -->
                  <DefaultIconsAccount v-if="!getAvatar() || imageError" />
                  <!--no profile-pic -->
                </div>
                <div class="badge__body">
                  <div class="text-2xl font-bold leading-6">{{author?.fullname}}</div>
                  <div
                    v-if="author?.profession"
                    class="text-sm"
                  >
                    {{author?.profession}}
                  </div>
                </div>
              </div>
              <div class="hidden sm:flex flex-col gap-2 items-end">
                <DefaultSocialList
                  :items="socialList()"
                />
                <div v-if="author?.email">
                  <NuxtLink :to="`mailto:${author?.email}`" class="link link--author-mail">{{author?.email}}</NuxtLink>
                </div>
              </div>
            </div>

            <div v-if="author?.biography" v-html="author?.biography" />

            <div class="sm:hidden flex flex-col gap-2 items-start">
              <DefaultSocialList
                :items="socialList()"
              />
              <div v-if="author?.email">
                <NuxtLink :to="`mailto:${author?.email}`" class="link link--author-mail">{{author?.email}}</NuxtLink>
              </div>
            </div>
          </div>
        </template>
        <template #right>
          <!-- CNC Ads -->
          <div class="advertisement-right-col h-full">
            <div class="sticky-sidebar-ad-wrapper">
              <DefaultAdsCncWrapper
                :position="UniFeAdsPosition.COLUMN_STICKY"
                :page="cncAdsPage"
              />
            </div>
          </div>
        </template>
      </DefaultTwoColumnsContent>
    </section>
  </DefaultPageContainer>
</template>

<script setup lang="ts">
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import type SocialItem from "~/libs/interfaces/SocialItem"
import {SocialList} from "~/libs/constants/SocialList"

// i18n routing
definePageMeta({
  name: 'newsroom-authorId',
})
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    cs: '/redakce/[authorId]',
    en: '/newsroom/[authorId]'
  }
})

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const cncAdsPage = CncAdsPage.AUTHOR

const authorId = route.params.authorId as string
const author = useRedactionConfig().getAuthorById(authorId)

// Set SEO meta
useSeoWrapper().setSeoMeta({
  title: author?.fullname,
})

/**
 * Author info
 */

const imageError = ref(false)

const replaceByDefault = () => {
  imageError.value = true
}

if (!author) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const getAvatar = () => {
  return author?.imageAsset?.urlWithProtocol || author?.avatarUrl
}

const socialList = ():SocialItem[] => {
  const list: SocialItem[] = []
  // Facebook
  if (author?.facebook) {
    list.push({
      name: SocialList.FACEBOOK,
      id: SocialList.FACEBOOK,
      url: author?.facebook
    })
  }
  // Instagram
  if (author?.instagram) {
    list.push({
      name: SocialList.INSTAGRAM,
      id: SocialList.INSTAGRAM,
      url: author?.instagram
    })
  }
  // Twitter
  if (author?.twitter) {
    list.push({
      name: SocialList.TWITTER,
      id: SocialList.TWITTER,
      url: author?.twitter
    })
  }
  // Linkedin
  if (author?.linkedin) {
    list.push({
      name: SocialList.LINKEDIN,
      id: SocialList.LINKEDIN,
      url: author?.linkedin
    })
  }
  // Telegram
  if (author?.telegram) {
    list.push({
      name: SocialList.TELEGRAM,
      id: SocialList.TELEGRAM,
      url: author?.telegram
    })
  }
  // Facebook
  if (author?.tiktok) {
    list.push({
      name: SocialList.TIKTOK,
      id: SocialList.TIKTOK,
      url: author?.tiktok
    })
  }
  return list
}

/**
 * On mounted - load data client
 */
onMounted(async () => {

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })

})
</script>
